import React, { useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Row,
  Col,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import TaxService from "../../../services/OrderingServices/TaxService";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Tax name is required")
    .min(2, "Tax name must be at least 2 characters"),
  rate: Yup.number()
    .required("Tax rate is required")
    .min(0, "Rate cannot be negative")
    .max(100, "Rate cannot exceed 100%"),
  calculationType: Yup.number().required("Calculation type is required"),
  description: Yup.string().nullable(),
  isActive: Yup.boolean(),
});

const TaxTypeForm = ({ modal, toggleModal, selectedTaxType, fetchTaxTypes }) => {
  const formik = useFormik({
    initialValues: {
      id: 0,
      name: "",
      rate: 0,
      calculationType: 1,
      description: "",
      isActive: true,
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        if (selectedTaxType?.id) {
          await TaxService.update(selectedTaxType.id, values);
          toast.success("Tax type updated successfully");
        } else {
          await TaxService.create(values);
          toast.success("Tax type created successfully");
        }
        fetchTaxTypes();
        toggleModal();
        resetForm();
      } catch (error) {
        console.error("Error saving tax type:", error);
        toast.error(error.response?.data?.message || "Failed to save tax type");
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Reset form when modal opens or selectedTaxType changes
  useEffect(() => {
    if (modal) {
      if (selectedTaxType) {
        // Map string representation back to numeric value if necessary
        const calcType = selectedTaxType.calculationType === "Percentage" ? 1 : 
                         selectedTaxType.calculationType === "Fixed Amount" ? 2 : 
                         (selectedTaxType.calculationType || 1);

        formik.setValues({
          id: selectedTaxType.id || 0,
          name: selectedTaxType.name || "",
          rate: selectedTaxType.rate || 0,
          calculationType: calcType,
          description: selectedTaxType.description || "",
          isActive: selectedTaxType.isActive ?? true,
        });
      } else {
        formik.resetForm();
      }
    }
  }, [modal, selectedTaxType]);

  return (
    <Modal isOpen={modal} toggle={toggleModal} centered backdrop="static">
      <ModalHeader toggle={toggleModal}>
        {selectedTaxType?.id ? "Edit Tax Type" : "Add New Tax Type"}
      </ModalHeader>
      <Form onSubmit={formik.handleSubmit}>
        <ModalBody className="p-4">
          <Row>
            <Col lg={12}>
              <FormGroup>
                <Label for="name" className="fw-medium">
                  Tax Name <span className="text-danger">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="e.g. VAT, Service Charge"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.name && formik.errors.name)}
                />
                <FormFeedback>{formik.errors.name}</FormFeedback>
              </FormGroup>
            </Col>

            <Col lg={12}>
              <FormGroup>
                <Label for="rate" className="fw-medium">
                  Tax Rate {formik.values.calculationType === 1 ? "(%)" : "(Fixed)"}{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  id="rate"
                  name="rate"
                  type="number"
                  placeholder="0.00"
                  step="0.01"
                  value={formik.values.rate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.rate && formik.errors.rate)}
                />
                <FormFeedback>{formik.errors.rate}</FormFeedback>
              </FormGroup>
            </Col>

            <Col lg={12}>
              <FormGroup>
                <Label for="calculationType" className="fw-medium">
                  Calculation Type <span className="text-danger">*</span>
                </Label>
                <Input
                  id="calculationType"
                  name="calculationType"
                  type="select"
                  value={formik.values.calculationType}
                  onChange={(e) => formik.setFieldValue("calculationType", Number(e.target.value))}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.calculationType && formik.errors.calculationType)}
                >
                  <option value={1}>Percentage</option>
                  <option value={2}>Fixed Amount</option>
                </Input>
                <FormFeedback>{formik.errors.calculationType}</FormFeedback>
              </FormGroup>
            </Col>

            <Col lg={12}>
              <FormGroup>
                <Label for="description" className="fw-medium">
                  Description
                </Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  rows="3"
                  placeholder="Enter details about this tax..."
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.description && formik.errors.description)}
                />
                <FormFeedback>{formik.errors.description}</FormFeedback>
              </FormGroup>
            </Col>

            <Col lg={12}>
              <FormGroup check className="mt-2">
                <Label check className="fw-medium">
                  <Input
                    type="checkbox"
                    name="isActive"
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                  />{" "}
                  Is Active
                </Label>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="bg-light">
          <Button color="light" onClick={toggleModal} disabled={formik.isSubmitting}>
            Cancel
          </Button>
          <Button color="success" type="submit" disabled={formik.isSubmitting} className="px-4">
            {formik.isSubmitting ? "Saving..." : selectedTaxType?.id ? "Update Tax" : "Save Tax"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default TaxTypeForm;
